/**
 * Sıradaki namaz vakti — üst başlıkta bir satır, dokununca günün tamamı.
 *
 * Vakitlerin nereden geldiği ve nasıl hesaplandığı `lib/namaz.ts`'te.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Moon } from 'lucide-react';
import type { Event } from '@/lib/types';
import { bugunTarih, dakika, sonrakiVakit, useNamazVakitleri } from '@/lib/namaz';
import { cn } from '@/lib/utils';

const kalanMetni = (dk: number) =>
  dk >= 60 ? `${Math.floor(dk / 60)} sa ${dk % 60} dk` : `${dk} dk`;

export function NamazVakti({ event, className }: { event?: Event; className?: string }) {
  const [acik, setAcik] = useState(false);
  const kap = useRef<HTMLDivElement>(null);
  /* Kalan süre dakikası geçtikçe tazelensin diye dakikada bir tik. */
  const [tik, setTik] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => setTik((n) => n + 1), 60_000);
    return () => window.clearInterval(id);
  }, []);

  const { data } = useNamazVakitleri(event);

  useEffect(() => {
    if (!acik) return;
    const disari = (e: PointerEvent) => {
      if (!kap.current?.contains(e.target as Node)) setAcik(false);
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setAcik(false);
    document.addEventListener('pointerdown', disari);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('pointerdown', disari);
      document.removeEventListener('keydown', esc);
    };
  }, [acik]);

  const gelecek = useMemo(() => (data ? sonrakiVakit(data) : null), [data, tik]);
  const bugunku = data?.find((g) => g.tarih === bugunTarih());

  if (!gelecek) return null;

  const simdi = new Date();
  const su = simdi.getHours() * 60 + simdi.getMinutes();

  return (
    <div ref={kap} className={cn('relative', className)}>
      <button
        type="button"
        onClick={() => setAcik((a) => !a)}
        aria-expanded={acik}
        className="flex items-center gap-1.5 text-sm opacity-90 transition-opacity hover:opacity-100"
      >
        <Moon className="h-3.5 w-3.5" aria-hidden />
        <span className="font-medium">{gelecek.vakit.ad}</span>
        <span>{gelecek.vakit.saat}</span>
        <span className="opacity-70">· {kalanMetni(gelecek.kalanDk)}</span>
      </button>

      {acik && bugunku && (
        <div
          className="absolute left-0 top-full z-40 mt-2 w-52 rounded-xl border border-border bg-white p-2 text-primary shadow-lg"
          role="dialog"
          aria-label="Bugünün namaz vakitleri"
        >
          <p className="px-2 pb-1 pt-0.5 text-[11px] uppercase tracking-wider text-muted-foreground">
            Bugün{event?.locationName ? ` · ${event.locationName}` : ''}
          </p>
          <ul className="space-y-0.5">
            {bugunku.vakitler.map((v) => {
              const gecti = dakika(v.saat) <= su;
              const siradaki = v.ad === gelecek.vakit.ad && !gelecek.yarin;
              return (
                <li
                  key={v.ad}
                  className={cn(
                    'flex items-center justify-between rounded-lg px-2 py-1.5 text-sm',
                    siradaki && 'bg-accent/20 font-medium ring-1 ring-accent/40',
                    gecti && !siradaki && 'text-muted-foreground',
                  )}
                >
                  <span>{v.ad}</span>
                  <span className="tabular-nums">{v.saat}</span>
                </li>
              );
            })}
          </ul>
          <p className="px-2 pb-0.5 pt-1.5 text-[10px] leading-snug text-muted-foreground">
            Diyanet takvimi. Teheccüd imsaktan 2 saat önce alınmıştır.
          </p>
        </div>
      )}
    </div>
  );
}
